import React from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import type { declineReason_softwareReleasesApprovers$key } from '@atlassian/jira-relay/src/__generated__/declineReason_softwareReleasesApprovers.graphql';
import type {
	declineReason_softwareReleasesApproversMutation,
	declineReason_softwareReleasesApproversMutation$data as mutationResponseData,
} from '@atlassian/jira-relay/src/__generated__/declineReason_softwareReleasesApproversMutation.graphql';
import { TextAreaInput } from '../../../../common/ui/textarea-input/index.tsx';
import messages from './messages.tsx';

interface Props {
	jiraVersionApprover: declineReason_softwareReleasesApprovers$key;
}

export const DeclineReason = ({ jiraVersionApprover }: Props) => {
	const { showFlag } = useFlagsService();
	const {
		declineReason,
		id: approverAri,
		user: { accountId },
	} = useFragment(
		graphql`
			fragment declineReason_softwareReleasesApprovers on JiraVersionApprover {
				id @required(action: THROW)
				declineReason
				user @required(action: THROW) {
					accountId @required(action: THROW)
				}
			}
		`,
		jiraVersionApprover,
	);
	const {
		data: { user },
	} = useCurrentUser();
	const canChangeDeclineReason = user.accountId === accountId;

	const [updateDeclineReason] = useMutation<declineReason_softwareReleasesApproversMutation>(
		graphql`
			mutation declineReason_softwareReleasesApproversMutation(
				$input: JiraVersionUpdateApproverDeclineReasonInput!
			) @raw_response_type {
				jira {
					updateJiraVersionApproverDeclineReason(input: $input)
						@optIn(to: "JiraVersionUpdateApproverDeclineReason") {
						success
						approver {
							id
							declineReason
						}
						errors {
							message
						}
					}
				}
			}
		`,
	);

	const onError = () => {
		fireErrorAnalytics({
			meta: {
				id: 'jira.releases.version-detail.approver.update-decline-reason',
				packageName: 'jiraSoftwareReleasesApprovers',
				teamName: 'fusion-solaris',
			},
			error: Error('Unable to update decline reason'),
			sendToPrivacyUnsafeSplunk: true,
		});
		showFlag({
			title: messages.updateDeclineReasonFailed,
			type: 'error',
		});
	};

	return (
		<TextAreaInput
			isReadOnlyMode={!canChangeDeclineReason}
			onConfirm={(newString) => {
				const newValue = newString === '' ? null : newString;
				updateDeclineReason({
					variables: {
						input: {
							approverId: approverAri,
							reason: newValue,
						},
					},
					onCompleted: (data: mutationResponseData) => {
						if (!data.jira?.updateJiraVersionApproverDeclineReason?.success) {
							onError();
						}
					},
					onError,
					optimisticResponse: {
						jira: {
							updateJiraVersionApproverDeclineReason: {
								success: true,
								errors: null,
								approver: {
									id: approverAri,
									declineReason: newValue,
								},
							},
						},
					},
				});
			}}
			title={messages.declineLabel}
			placeholderText={messages.noDeclinePlaceholder}
			confirmButtonAriaLabel={messages.declineEditConfirmLabel}
			cancelButtonAriaLabel={messages.declineEditCancelLabel}
			textAreaFocusAriaLabel={messages.declineAriaLabel}
			editIconAriaLabel={messages.declineAriaLabel}
			defaultValue={declineReason ?? ''}
		/>
	);
};
